// frontend/src/pages/OrderDetailPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRight, Copy, CheckCircle, Clock, Truck, Package, XCircle,
  CreditCard, User, Phone, MapPin, Calendar, DollarSign, FileText,
  Upload, Eye, Download, Check, AlertCircle, Loader2, ShoppingBag, Banknote
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { PageSEO } from '../components/SEO/PageSEO';
import config from '../config';
import { toast } from 'react-hot-toast';

export const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/orders';
  const { user, isAuthenticated } = useAuthStore();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tempReceiptFile, setTempReceiptFile] = useState(null);
  const [showReplaceOption, setShowReplaceOption] = useState(false);

  useEffect(() => {
        // اگر لاگین نیست، کاری نکن
    if (!isAuthenticated) return;
    fetchOrder();
  }, [id, isAuthenticated]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/detail/${id}/`), {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache' // ✅ جلوگیری از کش شدن پاسخ
      });

      if (!response.ok) throw new Error('خطا در دریافت اطلاعات سفارش');

      const data = await response.json();
      console.log('📦 Order data fetched:', data);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('خطا در دریافت اطلاعات سفارش');
    } finally {
      setLoading(false);
    }
  };

  // ========== تابع کپی شماره سفارش ==========
  const copyOrderId = () => {
    navigator.clipboard.writeText(order?.order_number || order?.id || '');
    toast.success('شماره سفارش کپی شد');
  };

  // ========== تابع حذف فیش ==========
  const handleRemoveReceipt = async () => {
    if (!window.confirm('آیا از حذف فیش واریزی اطمینان دارید؟')) {
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${order.id}/remove-receipt/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در حذف فیش');
      }

      toast.success('فیش واریزی با موفقیت حذف شد');
      setOrder(prev => ({ ...prev, receipt: null, status: 'pending' }));
    } catch (error) {
      console.error('Error removing receipt:', error);
      toast.error(error.message || 'خطا در حذف فیش');
    } finally {
      setUploading(false);
    }
  };

  // ========== تابع آپلود فیش واریزی ==========
  const handleUploadReceipt = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('فقط فایل‌های تصویری و PDF مجاز هستند');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم فایل نباید بیشتر از 5 مگابایت باشد');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setTempReceiptFile({
        file: file,
        preview: reader.result,
        name: file.name,
        size: file.size
      });
      toast.success('فایل با موفقیت انتخاب شد');
    };
    reader.readAsDataURL(file);
  };

  // ========== تابع تایید و ارسال نهایی فیش ==========
  // ========== تابع تایید و ارسال نهایی فیش ==========
  const handleConfirmReceipt = async () => {
    if (!tempReceiptFile) {
      toast.error('لطفاً ابتدا فایل را انتخاب کنید');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('receipt', tempReceiptFile.file);

      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${order.id}/upload-receipt/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در آپلود فیش');
      }

      const data = await response.json();
      toast.success('فیش واریزی با موفقیت آپلود شد');

      // ✅ اصلاح: به‌روزرسانی کامل order با داده‌های جدید
      setOrder(prev => ({
        ...prev,
        receipt: data.receipt_url,
        status: 'pending_confirmation'
      }));

      setTempReceiptFile(null);
      setShowReplaceOption(false);

      // ✅ اضافه کردن: مجدداً سفارش را از سرور دریافت کن تا همه داده‌ها به‌روز شوند
      await fetchOrder();

    } catch (error) {
      console.error('Error uploading receipt:', error);
      toast.error(error.message || 'خطا در آپلود فیش');
    } finally {
      setUploading(false);
    }
  };

  // ========== تابع لغو انتخاب فایل ==========
  const handleCancelFileSelection = () => {
    setTempReceiptFile(null);
    setShowReplaceOption(false);
    toast.info('انتخاب فایل لغو شد');
  };

  // ========== تابع پرداخت آنلاین ==========
  const handleOnlinePayment = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${order.id}/pay/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در پرداخت');
      }

      const data = await response.json();

      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        toast.success('پرداخت با موفقیت انجام شد');
        fetchOrder();
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(error.message || 'خطا در پرداخت');
    } finally {
      setIsProcessing(false);
    }
  };

  // ========== تابع تغییر روش پرداخت ==========
  const handlePaymentMethodChange = async (newMethod) => {
    if (updatingStatus) return;

    if (!window.confirm(`آیا از تغییر روش پرداخت اطمینان دارید؟`)) {
      return;
    }

    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${order.id}/update-payment-method/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ payment_method: newMethod })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در تغییر روش پرداخت');
      }

      toast.success('روش پرداخت با موفقیت تغییر یافت');
      await fetchOrder();
    } catch (error) {
      console.error('Error changing payment method:', error);
      toast.error(error.message || 'خطا در تغییر روش پرداخت');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ========== تابع تغییر وضعیت سفارش (ادمین/فروشنده) ==========
  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`آیا از تغییر وضعیت سفارش به "${getStatusText(newStatus)}" اطمینان دارید؟`)) {
      return;
    }

    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${order.id}/update-tracking/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در تغییر وضعیت');
      }

      const data = await response.json();
      toast.success(`وضعیت سفارش به "${getStatusText(newStatus)}" تغییر یافت`);
      fetchOrder();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'خطا در تغییر وضعیت سفارش');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ========== تابع تایید پرداخت (ادمین) ==========
  const handleConfirmPayment = async () => {
    if (!window.confirm('آیا از تایید پرداخت این سفارش اطمینان دارید؟')) {
      return;
    }

    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${order.id}/confirm-payment/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'خطا در تایید پرداخت');
      }

      toast.success('پرداخت با موفقیت تایید شد');
      fetchOrder();
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error(error.message || 'خطا در تایید پرداخت');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ========== توابع کمکی ==========
  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'در انتظار پرداخت',
      'pending_confirmation': 'در انتظار تایید پرداخت',
      'paid': 'پرداخت شده',
      'processing': 'در حال پردازش',
      'shipped': 'ارسال شده',
      'delivered': 'تحویل داده شده',
      'completed': 'تکمیل شده',
      'cancelled': 'لغو شده',
    };
    return statusMap[status] || status;
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'pending': Clock,
      'pending_confirmation': Clock,
      'paid': CreditCard,
      'processing': Package,
      'shipped': Truck,
      'delivered': CheckCircle,
      'completed': CheckCircle,
      'cancelled': XCircle,
    };
    return iconMap[status] || Clock;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'pending_confirmation': 'bg-orange-100 text-orange-800 border-orange-200',
      'paid': 'bg-blue-100 text-blue-800 border-blue-200',
      'processing': 'bg-purple-100 text-purple-800 border-purple-200',
      'shipped': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'delivered': 'bg-green-100 text-green-800 border-green-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusSteps = () => {
    const allSteps = [
      { status: 'pending', label: 'ثبت سفارش' },
      { status: 'paid', label: 'پرداخت' },
      { status: 'processing', label: 'پردازش' },
      { status: 'shipped', label: 'ارسال' },
      { status: 'delivered', label: 'تحویل' },
    ];

    const currentIndex = allSteps.findIndex(step => step.status === order?.status);
    return allSteps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      active: index === currentIndex
    }));
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '۰';
    return Number(price).toLocaleString('fa-IR');
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      online: 'پرداخت آنلاین',
      cash_on_delivery: 'پرداخت در محل',
      card_to_card: 'کارت به کارت',
      manual: 'ثبت دستی',
    };
    return methods[method] || method || '-';
  };

  const getReceiptUrl = () => {
    return order?.receipt ? config.getMediaUrl(order.receipt) : '';
  };

  // ========== بررسی دسترسی‌ها ==========
  const isAdmin = user?.role === 'admin';
  const isSeller = user?.role === 'seller';
  const isCustomer = user?.role === 'customer' || user?.role === 'user';
  const canManageOrder = isAdmin || isSeller;
  const isOrderOwner = order?.user?.id === user?.id || order?.user_detail?.id === user?.id;

  // ========== وضعیت‌های قابل تغییر توسط ادمین/فروشنده ==========
  const getAvailableStatuses = () => {
    const statuses = {
      'paid': ['processing'],
      'processing': ['shipped'],
      'shipped': ['delivered'],
      'delivered': [],
      'completed': [],
      'cancelled': [],
    };
    return statuses[order?.status] || [];
  };

  // ============================================================
  // ✅ بخش پرداخت (مشتری) - کامل با آپلود فیش
  // ============================================================
  const renderPaymentSection = () => {
    // بررسی دسترسی‌ها
    if (!isCustomer || !isOrderOwner) return null;

    // اگر وضعیت پرداخت شده یا تکمیل شده باشد
    if (['paid', 'processing', 'shipped', 'delivered', 'completed'].includes(order?.status)) {
      return null;
    }

    // اگر سفارش لغو شده باشد
    if (order?.status === 'cancelled') return null;

    // ==========================================================
    // ✅ اگر روش پرداخت manual است
    // ==========================================================
    if (order?.payment_method === 'manual') {
      return (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            روش پرداخت
          </h4>
          <p className="text-sm text-blue-700 mb-3">
            مبلغ قابل پرداخت: <span className="font-bold">{formatPrice(order?.final_amount || order?.total_amount)} تومان</span>
          </p>
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-700">
              این سفارش با روش <span className="font-bold">ثبت دستی</span> ایجاد شده است.
              لطفاً برای تکمیل پرداخت با پشتیبانی تماس بگیرید.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              شماره تماس: {config.CONTACT_PHONE || '021-88315658'}
            </p>
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => handlePaymentMethodChange('online')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-sm"
              >
                تغییر به پرداخت آنلاین
              </button>
              <button
                onClick={() => handlePaymentMethodChange('card_to_card')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-sm"
              >
                تغییر به کارت به کارت
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ==========================================================
    // ✅ روش‌های پرداخت معمولی
    // ==========================================================
    const currentMethod = order?.payment_method || 'online';
    const isPending = order?.status === 'pending';
    const isPendingConfirmation = order?.status === 'pending_confirmation';
    const canChangePaymentMethod = isPending; // فقط در حالت pending می‌توان روش را تغییر داد
    const receiptUrl = order?.receipt ? config.getMediaUrl(order.receipt) : '';

    return (
      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          روش پرداخت
        </h4>

        <p className="text-sm text-blue-700 mb-3">
          مبلغ قابل پرداخت: <span className="font-bold">{formatPrice(order?.final_amount || order?.total_amount)} تومان</span>
        </p>

        <div className="space-y-3">
          {/* ✅ پیام در انتظار تایید - بدون خروج از تابع */}
          {isPendingConfirmation && (
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-orange-600" />
                <div>
                  <h4 className="font-medium text-orange-800">در انتظار تایید پرداخت</h4>
                  <p className="text-sm text-orange-700">
                    فیش واریزی شما در حال بررسی است. در صورت نیاز می‌توانید فیش را مشاهده، جایگزین یا حذف کنید.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================== */}
          {/* روش پرداخت آنلاین */}
          {/* ========================================================== */}
          <div className={`flex items-center gap-3 p-3 rounded-lg border-2 ${currentMethod === 'online' ? 'border-blue-500 bg-blue-100' : 'border-gray-200'}`}>
            <input
              type="radio"
              name="payment_method"
              checked={currentMethod === 'online'}
              onChange={() => handlePaymentMethodChange('online')}
              className="w-4 h-4 text-blue-600"
              disabled={!canChangePaymentMethod} // فقط در حالت pending فعال است
            />
            <CreditCard className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <span className="font-medium">پرداخت آنلاین</span>
              <p className="text-xs text-gray-500">پرداخت از طریق درگاه زرین‌پال</p>
            </div>
            {currentMethod === 'online' && canChangePaymentMethod && (
              <button
                onClick={handleOnlinePayment}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition text-sm"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'پرداخت'}
              </button>
            )}
          </div>

          {/* ========================================================== */}
          {/* ✅ روش کارت به کارت با بخش آپلود فیش در داخل */}
          {/* ========================================================== */}
          <div className={`rounded-lg border-2 overflow-hidden ${currentMethod === 'card_to_card' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex items-center gap-3 p-3">
              <input
                type="radio"
                name="payment_method"
                checked={currentMethod === 'card_to_card'}
                onChange={() => handlePaymentMethodChange('card_to_card')}
                className="w-4 h-4 text-green-600"
                disabled={!canChangePaymentMethod} // فقط در حالت pending فعال است
              />
              <Banknote className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <span className="font-medium">کارت به کارت</span>
                <p className="text-xs text-gray-500">واریز به شماره کارت و آپلود فیش</p>
              </div>
              {currentMethod === 'card_to_card' && (
                <span className="text-xs text-green-600">✓ انتخاب شده</span>
              )}
            </div>

            {/* ========================================================== */}
            {/* بخش آپلود فیش - فقط وقتی کارت به کارت انتخاب شده باشد */}
            {/* ========================================================== */}
            {currentMethod === 'card_to_card' && (
              <div className="p-4 bg-yellow-50 border-t border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm text-yellow-800">
                    <Upload className="w-4 h-4" />
                    <span className="font-medium">آپلود فیش واریزی</span>
                  </div>
                  {order?.receipt && (
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                      ✓ آپلود شده
                    </span>
                  )}
                </div>

                <div className="text-sm text-yellow-700 mb-3">
                  <p>شماره کارت: <span className="font-bold" dir="ltr">6037-9918-1234-5678</span></p>
                  <p>مبلغ: <span className="font-bold">{formatPrice(order?.final_amount || order?.total_amount)} تومان</span></p>
                  <p className="text-xs text-gray-500 mt-1">پس از واریز، فیش را آپلود کنید تا پرداخت شما تایید شود.</p>
                </div>

                {/* ========================================================== */}
                {/* ✅ حالت ۱: فیش قبلاً آپلود شده (با دکمه‌های کامل) */}
                {/* ========================================================== */}
                {order?.receipt && !tempReceiptFile ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm text-green-600 flex-wrap p-3 bg-green-50 rounded-lg border border-green-200">
                      <Check className="w-5 h-5" />
                      <span>فیش واریزی با موفقیت آپلود شد</span>

                      <a
                        href={receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        مشاهده فیش
                      </a>

                      {/* ✅ دکمه‌های جایگزینی و حذف - در همه وضعیت‌ها به جز paid نمایش داده می‌شوند */}
                      {/* این شرط اجازه می‌دهد در pending_confirmation نیز دکمه‌ها نمایش داده شوند */}
                      {!['paid', 'processing', 'shipped', 'delivered', 'completed'].includes(order?.status) && (
                        <>
                          <button
                            onClick={() => setShowReplaceOption(true)}
                            className="text-orange-600 hover:text-orange-700 flex items-center gap-1 text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            جایگزینی
                          </button>
                          <button
                            onClick={handleRemoveReceipt}
                            disabled={uploading}
                            className="text-red-600 hover:text-red-700 flex items-center gap-1 text-sm"
                          >
                            {uploading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                            حذف
                          </button>
                        </>
                      )}

                      {/* برچسب در انتظار تایید */}
                      {isPendingConfirmation && (
                        <span className="text-orange-600 text-xs bg-orange-50 px-2 py-1 rounded-full">
                          در انتظار تایید ادمین
                        </span>
                      )}
                    </div>

                    {/* گزینه جایگزینی فیش */}
                    {showReplaceOption && (
                      <div className="p-3 bg-white rounded-lg border border-orange-200">
                        <p className="text-sm text-gray-600 mb-2">برای جایگزینی، فایل جدید را انتخاب کنید:</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={handleUploadReceipt}
                              disabled={uploading}
                              className="hidden"
                            />
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition text-sm">
                              <Upload className="w-4 h-4" />
                              انتخاب فایل جدید
                            </span>
                          </label>
                          <button
                            onClick={() => setShowReplaceOption(false)}
                            className="text-gray-500 hover:text-gray-700 text-sm"
                          >
                            انصراف
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : tempReceiptFile ? (
                  /* ========================================================== */
                  /* ✅ حالت ۲: فایل انتخاب شده اما هنوز ارسال نشده (پیش‌نمایش) */
                  /* ========================================================== */
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">پیش‌نمایش فایل انتخاب شده</span>
                        <span className="text-xs text-gray-500">{(tempReceiptFile.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {tempReceiptFile.preview && tempReceiptFile.file.type.startsWith('image/') ? (
                          <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                            <img
                              src={tempReceiptFile.preview}
                              alt="پیش‌نمایش فیش"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-20 h-20 rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50 flex-shrink-0">
                            <FileText className="w-10 h-10 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800 truncate">{tempReceiptFile.name}</p>
                          <p className="text-xs text-gray-500">برای تایید، دکمه "ارسال فیش" را بزنید</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={handleUploadReceipt}
                            disabled={uploading}
                            className="hidden"
                          />
                          <span className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                            <Upload className="w-4 h-4" />
                            انتخاب مجدد
                          </span>
                        </label>
                        <button
                          onClick={handleCancelFileSelection}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          لغو
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={handleConfirmReceipt}
                      disabled={uploading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition text-sm"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          در حال ارسال...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          تایید و ارسال فیش
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  /* ========================================================== */
                  /* ✅ حالت ۳: هیچ فایلی انتخاب نشده */
                  /* ========================================================== */
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleUploadReceipt}
                        disabled={uploading}
                        className="hidden"
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white rounded-lg transition text-sm">
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            در حال آپلود...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            انتخاب فایل
                          </>
                        )}
                      </span>
                    </label>
                    <span className="text-xs text-gray-500">(فایل تصویری یا PDF - حداکثر ۵ مگابایت)</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ========================================================== */}
          {/* روش پرداخت در محل */}
          {/* ========================================================== */}
          <div className={`flex items-center gap-3 p-3 rounded-lg border-2 ${currentMethod === 'cash_on_delivery' ? 'border-purple-500 bg-purple-100' : 'border-gray-200'}`}>
            <input
              type="radio"
              name="payment_method"
              checked={currentMethod === 'cash_on_delivery'}
              onChange={() => handlePaymentMethodChange('cash_on_delivery')}
              className="w-4 h-4 text-purple-600"
              disabled={!canChangePaymentMethod} // فقط در حالت pending فعال است
            />
            <Truck className="w-5 h-5 text-purple-600" />
            <div className="flex-1">
              <span className="font-medium">پرداخت در محل</span>
              <p className="text-xs text-gray-500">پرداخت پس از تحویل سفارش</p>
            </div>
            {currentMethod === 'cash_on_delivery' && (
              <span className="text-xs text-purple-600">✓ انتخاب شده</span>
            )}
          </div>

          {/* وضعیت در حال تغییر */}
          {updatingStatus && (
            <div className="flex justify-center py-2">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="mr-2 text-sm text-gray-600">در حال تغییر...</span>
            </div>
          )}
        </div>

        {/* اطلاعات اضافی برای پرداخت در محل */}
        {currentMethod === 'cash_on_delivery' && (
          <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800">
              مبلغ {formatPrice(order?.final_amount || order?.total_amount)} تومان پس از تحویل سفارش دریافت می‌شود.
            </p>
          </div>
        )}
      </div>
    );
  }; // ✅ اینجا تابع تمام می‌شود

  // ========== نمایش بخش تایید پرداخت (ادمین) ==========
  const renderPaymentConfirmation = () => {
    if (!isAdmin) return null;
    if (order?.status !== 'pending_confirmation') return null;
    if (!order?.receipt) return null;

    return (
      <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
        <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          تایید پرداخت
        </h4>
        <p className="text-sm text-green-700 mb-3">
          مشتری فیش واریزی را آپلود کرده است. لطفاً پرداخت را تایید کنید.
        </p>
        <div className="flex items-center gap-3">
          <a
            href={config.getMediaUrl(order.receipt)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-sm"
          >
            <Eye className="w-4 h-4" />
            مشاهده فیش
          </a>
          <button
            onClick={handleConfirmPayment}
            disabled={updatingStatus}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition text-sm"
          >
            {updatingStatus ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4" />
                تایید پرداخت
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ========== نمایش وضعیت‌های قابل تغییر ==========
  const renderStatusActions = () => {
    if (!canManageOrder) return null;

    const availableStatuses = getAvailableStatuses();
    if (availableStatuses.length === 0) return null;

    return (
      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-800 mb-3">تغییر وضعیت سفارش</h4>
        <div className="flex flex-wrap gap-2">
          {availableStatuses.map(status => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              disabled={updatingStatus}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition text-sm flex items-center gap-2"
            >
              {updatingStatus ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {status === 'paid' && <CreditCard className="w-4 h-4" />}
                  {status === 'processing' && <Package className="w-4 h-4" />}
                  {status === 'shipped' && <Truck className="w-4 h-4" />}
                  {status === 'delivered' && <CheckCircle className="w-4 h-4" />}
                  {status === 'cancelled' && <XCircle className="w-4 h-4" />}
                  {status === 'completed' && <CheckCircle className="w-4 h-4" />}
                  تغییر به {getStatusText(status)}
                </>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ========== دکمه‌های بازگشت ==========
  const renderBackButtons = () => {
    return (
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-gray-200 pt-6">
        {/* دکمه بازگشت به صفحه قبلی */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition px-4 py-2 rounded-lg hover:bg-gray-50"
        >
          <ArrowRight className="w-4 h-4" />
          بازگشت به صفحه قبلی
        </button>

        {/* دکمه رفتن به لیست سفارشات */}
        <button
          onClick={() => navigate(isAdmin ? '/admin/orders' : isSeller ? '/seller/orders' : '/orders')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition px-4 py-2 rounded-lg hover:bg-blue-50"
        >
          <ShoppingBag className="w-4 h-4" />
          مشاهده همه سفارشات
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">سفارش یافت نشد</h2>
          <p className="text-gray-600">سفارش مورد نظر شما وجود ندارد</p>
          {renderBackButtons()}
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(order.status);
  const steps = getStatusSteps();

  return (
    <>
      <PageSEO title={`سفارش #${order.order_number || order.id.toString().slice(0, 8)}`} noindex={true} />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    سفارش #{order.order_number || order.id.toString().slice(0, 8)}
                  </h1>
                  <button
                    onClick={copyOrderId}
                    className="text-gray-400 hover:text-gray-600"
                    title="کپی شماره سفارش"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  تاریخ ثبت: {formatDate(order.created_at)}
                </p>
              </div>
              <div className={`px-4 py-2 rounded-full border flex items-center gap-2 ${getStatusColor(order.status)}`}>
                <StatusIcon className="w-4 h-4" />
                <span className="font-medium">{getStatusText(order.status)}</span>
              </div>
            </div>

            {/* وضعیت سفارش (نوار مراحل) */}
            <div className="mt-6">
              <div className="flex items-center justify-between relative">
                {steps.map((step, index) => (
                  <div key={step.status} className="flex-1 flex items-center">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step.completed
                        ? 'bg-green-500 border-green-500 text-white'
                        : step.active
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'bg-gray-200 border-gray-300 text-gray-400'
                        }`}>
                        {step.completed ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <span className="text-xs">{index + 1}</span>
                        )}
                      </div>
                      <span className={`text-xs mt-1 text-center ${step.completed ? 'text-green-600' : step.active ? 'text-blue-600 font-medium' : 'text-gray-400'
                        }`}>
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${step.completed ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* اطلاعات سفارش */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-blue-600" />
                اطلاعات مشتری
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">نام:</span>
                  <span className="font-medium">{order.recipient_name || order.user_detail?.full_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">شماره تماس:</span>
                  <span className="font-medium" dir="ltr">{order.recipient_phone || order.user_detail?.phone || '-'}</span>
                </div>
                {order.user_detail?.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">ایمیل:</span>
                    <span className="font-medium">{order.user_detail.email}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                اطلاعات ارسال
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">آدرس:</span>
                  <span className="font-medium text-left">{order.shipping_address || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">شهر:</span>
                  <span className="font-medium">{order.city || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">استان:</span>
                  <span className="font-medium">{order.province || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">کد پستی:</span>
                  <span className="font-medium">{order.postal_code || '-'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* لیست محصولات */}
          <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-blue-600" />
              محصولات سفارش
            </h3>
            <div className="space-y-3">
              {order.items?.map((item) => (
                <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{item.product_name || item.product?.name}</div>
                    <div className="text-sm text-gray-500">
                      تعداد: {item.quantity} عدد
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-blue-600">
                      {formatPrice(item.price)} تومان
                    </div>
                    <div className="text-xs text-gray-400">
                      جمع: {formatPrice(item.price * item.quantity)} تومان
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between text-lg font-bold">
                <span>جمع کل:</span>
                <span className="text-blue-600">{formatPrice(order.final_amount || order.total_amount)} تومان</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>تخفیف:</span>
                  <span>- {formatPrice(order.discount_amount)} تومان</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>روش پرداخت:</span>
                <span>
                  {order.payment_method === 'online' && 'پرداخت آنلاین'}
                  {order.payment_method === 'cash_on_delivery' && 'پرداخت در محل'}
                  {order.payment_method === 'card_to_card' && 'کارت به کارت'}
                  {order.payment_method === 'manual' && 'ثبت دستی'}
                </span>
              </div>
              {order.is_manual_price && (
                <div className="flex justify-between text-sm text-orange-600 mt-1">
                  <span>نوع قیمت:</span>
                  <span>قیمت توافقی</span>
                </div>
              )}
            </div>
          </div>

          {/* یادداشت‌ها */}
          {(order.note || order.admin_note) && (
            <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
              {order.note && (
                <div className="mb-3">
                  <h4 className="font-medium text-gray-700 mb-1">یادداشت مشتری:</h4>
                  <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">{order.note}</p>
                </div>
              )}
              {order.admin_note && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">یادداشت ادمین:</h4>
                  <p className="text-gray-600 text-sm bg-blue-50 p-3 rounded-lg">{order.admin_note}</p>
                </div>
              )}
            </div>
          )}

          {/* ========== بخش اقدامات ========== */}
          {renderPaymentSection()}
          {renderPaymentConfirmation()}
          {renderStatusActions()}

          {/* ========== دکمه‌های بازگشت ========== */}
          {renderBackButtons()}
        </div>
      </div>
    </>
  );
};